import {
  Header as TameeniHeader,
  HeaderLogo,
  HeaderSideNav,
  LanguageToggle,
  UserProfile,
} from '@tameeni/header'
import { useAuth } from 'react-oidc-context'
import { useHeader } from '@/features/main-layout/hooks/use-header'
import { useLogin } from '@/features/authentication'
import {
  useChangeLocale,
  useCurrentLocale,
  useScopedI18n,
} from '@/locales/client'
import { getBaseUrl } from '../utils/url-helpers'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useTrackingEvents } from '@/features/tracking-events'
import { getEnv } from '@/lib/config/runtime-env'

export default function Header() {
  const { isAuthenticated, user } = useAuth()
  const locale = useCurrentLocale()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const changeLocale = useChangeLocale({ preserveSearchParams: true })
  const { signIn, signOut } = useLogin()
  const t = useScopedI18n('common.links')
  const headerT = useScopedI18n('common.header')

  const { sideNavItems, socialLinks } = useHeader(isAuthenticated)

  const { trackNavigationMenu } = useTrackingEvents()

  const motorBaseUrl = getEnv().MOTOR_BASE_URL

  const handleSignIn = () => {
    trackNavigationMenu({ product: 'mmp', selection: 'login_click' })
    const currentUrl =
      location.pathname +
      (searchParams.toString() ? `?${searchParams.toString()}` : '')
    signIn({ postLoginRedirectionUrl: currentUrl })
  }

  const handleSignOut = () => {
    trackNavigationMenu({ product: 'mmp', selection: 'logout_click' })
    signOut()
  }

  return (
    <TameeniHeader>
      <div className='box flex h-16 items-center gap-4'>
        <HeaderSideNav
          androidAppUrl='https://play.google.com/store/apps/details?id=com.tameeni.app'
          appGalleryUrl='https://appgallery.huawei.com/#/app/C101173509'
          direction={locale === 'ar' ? 'right' : 'left'}
          downloadAppLabel={headerT('downloadApp')}
          followUsLabel={headerT('followUs')}
          iosAppUrl='https://apps.apple.com/app/id1564818896'
          logo={locale === 'ar' ? 'TameeniLogoAR' : 'TameeniLogoEN'}
          logoClassName='w-[165px]'
          sideNav={sideNavItems}
          socialLinks={socialLinks}
        />
        <div className='ms-0 grow md:ms-4'>
          <HeaderLogo
            label={t('medical_malpractice')}
            link={getBaseUrl('/mmp', locale)}
            logoClassName='w-[165px]'
            mainLogo={locale === 'ar' ? 'TameeniLogoAR' : 'TameeniLogoEN'}
          />
        </div>
        <div className='contents' data-testid='home-language-toggle'>
          <LanguageToggle
            label={headerT('languageToggle')}
            onLanguageChange={() => {
              const newLocale = locale === 'en' ? 'ar' : 'en'
              trackNavigationMenu({
                product: 'mmp',
                selection: `language_${newLocale}`,
              })
              changeLocale(newLocale)
            }}
          />
        </div>
        <div className='contents' data-testid='home-login-btn'>
          <UserProfile
            loginLabel={headerT('login')}
            logoutLabel={headerT('logout')}
            onLogin={handleSignIn}
            onLogout={handleSignOut}
            profileMenuItems={[
              {
                label: headerT('myAccount'),
                icon: '',
                link: `/${locale}/dashboard`,
              },
              {
                label: headerT('accountSettings'),
                icon: '',
                link: `${motorBaseUrl}/motorapp/Individual/ProfilePage/Settings`,
              },
            ]}
            isLoggedIn={isAuthenticated}
            user={{
              id: user?.profile?.IdentityNumber as string,
              name: user?.profile?.UserName as string,
            }}
          />
        </div>
      </div>
    </TameeniHeader>
  )
}
